import { User } from '../models/user';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ITaskList, Task } from '../models/task';
import { SubjectService } from './subject.service';
import { BaseRestService } from './base-rest.service';
import { TaskStatusService, TaskTypeService } from './task-type.service';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { ListFilterItem, ListSortOrderItem } from './listSortAndFilterHelpers';
import { FileService } from './file.service';
import { DefaultDataService } from './default-data.service';
import {
  MessageBoxComponent,
  MessageBoxParameters,
} from 'app/components/messageBox/messageBox.component';
import { MatDialog } from '@angular/material/dialog';
import { SubjectTypeService } from './subject-type.service';
import { EventService } from './event.service';
import { messageService } from './messages/message.module';
import { SubjectType } from '../models/subject-type';

export enum enumTaskSelection {
  AllTasks = 0,
  MyTasks = 1,
  SubjectTasks = 2,
  UserTasks = 3,
  CreatedTasks = 4,
  FilterByUserTasks = 5,
}
@Injectable({ providedIn: 'root' })
export class TaskService extends BaseRestService<Task, ITaskList> {
  filters: ListFilterItem[] = [
    new ListFilterItem('All', 'assignment', true),
    new ListFilterItem('Open', 'folder_open'),
    new ListFilterItem('Due', 'warning'),
    new ListFilterItem('Late', 'assignment_late'),
    new ListFilterItem('Abandoned', 'assignment_return'),
    new ListFilterItem('Completed', 'assignment_turned_in'),
    new ListFilterItem('Important', 'error'),
    new ListFilterItem('Starred', 'star'),
  ];

  sortOrders: ListSortOrderItem[] = [
    new ListSortOrderItem('Task Status', '', 1, true),
    new ListSortOrderItem('Date Due', ''),
    new ListSortOrderItem('Date Created', ''),
    new ListSortOrderItem('Date Completed', ''),
  ];

  subjectStash = [];

  onNewTaskClicked: Subject<Task> = new Subject();
  onApplyFilter: Subject<ListFilterItem> = new Subject();
  onApplySortOrder: Subject<ListSortOrderItem> = new Subject();
  onApplySearchTerm: Subject<string> = new Subject();

  taskSelection = enumTaskSelection.MyTasks;
  taskSelectionUsers: string;
  taskSelectionOrgId?: number=-1;
  subjectUid: string;
  currentSortOrder: ListSortOrderItem;

  personSubjectType: SubjectType;

  constructor(
    http: HttpClient,
    private dialog: MatDialog,
    private userService: UserService,
    private taskStatusService: TaskStatusService,
    private taskTypeService: TaskTypeService,
    private subjectService: SubjectService,
    private eventService: EventService,
    private fileService: FileService,
    private defaultDataService: DefaultDataService,
    private subjectTypeService: SubjectTypeService,
    protected router: Router
  ) {
    super(TaskService, http, 'api/tasks', 'tasks', router);

    // we need to get the person subject type as we filter by person subject type for prompt to email etc
    // we do it here in the service so we are not creating multiple subscriptions for each task list item
    this.subjectTypeService
      .getByName('person')
      .pipe(take(1))
      .subscribe((subjectType) => {
        this.personSubjectType = subjectType;
      });
  }

  get personSubjectTypeId() {
    return this.personSubjectType ? this.personSubjectType.id : 2;
  }

  afterSave(entity) {
    // console.log('Task Saved, Getting new tasks & Updating User Task Stats');
    this.eventService.updateTaskStats();
  }

  setTaskSelection(
    taskSelection: enumTaskSelection,
    users?: string,
    subjectId?: string,
    orgId?:number,
  ) {
    this.taskSelection = taskSelection;
    this.taskSelectionUsers = users || '';
    if (orgId )
      this.taskSelectionOrgId = orgId;
    else 
      this.taskSelectionOrgId=-1;
    if (subjectId) {
      this.subjectUid = subjectId;
    }
  }

  getTasks() {
    let params = '';
    if (this.taskSelection === enumTaskSelection.MyTasks) {
      params = 'actionsForUser=' + this.userService.getCurrentUserUid();
    } else if (
      this.taskSelection === enumTaskSelection.SubjectTasks &&
      this.subjectUid
    ) {
      params = 'subjectUid=' + this.subjectUid;
    } else if (this.taskSelectionUsers && this.taskSelectionUsers.length > 0) {
      params = 'users=' + this.taskSelectionUsers;
    }
    this.getAll(null, params); // This call will trigger the OnChanged event which will update lists etc
  }

  applyDataFilter(data: Task[]): Task[] {
    const selectedUsers = this.userService.selectedUsers || [];
    
    if (this.taskSelectionOrgId>0)
    {
      data = data.filter((i) => i.organisationId==this.taskSelectionOrgId);
    }

    switch (this.taskSelection) {
      case enumTaskSelection.FilterByUserTasks:
        return data.filter(
          (i) => i.actionForId && selectedUsers.indexOf(i.actionForId) !== -1
        );
      case enumTaskSelection.CreatedTasks:
        return data.filter(
          (i) => i.createdById && selectedUsers.indexOf(i.createdById) !== -1
        );
      default:
        return data;
    }
  }

  markTaskAsCompleted(task: Task, subjectUIDList: string[]) {
    // this.debug('Updating:', task);
    // this.debug('Entity to update:', JSON.stringify(task));
    const taskCompleted = new TaskComplete();
    taskCompleted.task = task;
    taskCompleted.subjectUIDList = subjectUIDList;

    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    this.http
      .put<MarkTaskAsCompletedResult>(
        '/api/tasks/completeTask',
        taskCompleted,
        options
      )
      .pipe(
        catchError(this.handleError<Task>(`Complete Task`, true)),
        tap((_) => this.getTasks())
      )
      .subscribe((result: MarkTaskAsCompletedResult) => {
        if (result && result.taskMarkedAsCompleted === true) {
          // this.getTasks();
          this.eventService.updateTaskStats();
          if (result.emailMergeDocumentsCreated > 0) {
            this.eventService.filesUpdated();
          }
          if (result.mailMergeDocumentsCreated > 0) {
            this.eventService.filesUpdated();

            console.log('Completed Task', task);
            let subjectTypeName: string;
            let subjectSummary: string;
            if (task.subject) {
              subjectTypeName = this.subjectTypeService.getName(
                task.subject.subjectTypeId
              );
              subjectSummary = task.subject.summary;
            } else {
              const cachedTask = this.cache.find((i) => i.id === task.id);
              if (cachedTask && cachedTask.subject) {
                subjectTypeName = this.subjectTypeService.getName(
                  cachedTask.subject.subjectTypeId
                );
                subjectSummary = cachedTask.subject.summary;
              }
            }

            subjectTypeName = subjectTypeName || 'subject';

            if (!this.defaultDataService.autoAttachMergedDocuments) {
              //  iterate through the file uris to build up a list of files to download and then delete
              const uris = result.mailMergeDocumentUris.split(',');
              const download$ = [];
              const delete$ = [];
              uris.forEach((uri) => {
                download$.push(
                  this.fileService.getDownloadLink(uri).pipe(
                    map((url: string) => {
                      window.open(url);
                    })
                  )
                );
                delete$.push(
                  this.http
                    .delete<string>(`/api/files/${btoa(uri)}`)
                    .pipe(map((x) => {}))
                );
              });

              //  process the list of obervables in order, first the downloads and then the deletes
              forkJoin(download$)
                .pipe(take(1))
                .subscribe((y) => {
                  //  display message re editing files
                  const info = new MessageBoxParameters();
                  info.title = 'Mail Merge Documents Downloaded';
                  info.message =
                    'The mail merge document(s) have been automatically downloaded for edit. ' +
                    `Once completed please remember to add the edited file(s) as attachments to this ${subjectTypeName}.`;
                  this.dialog
                    .open(MessageBoxComponent, { data: info })
                    .afterClosed()
                    .subscribe((d) => {
                      // delete the mail merge files in storage
                      forkJoin(delete$)
                        .pipe(take(1))
                        .subscribe((z) => {
                          //  update the file list following changes
                          this.fileService.getFiles();
                        });
                    });
                });
            } else {
              let sub = `this subject`;
              if (subjectSummary) {
                sub = `the ${subjectTypeName}: ${task.subject.summary}`;
              }
              messageService.info(
                `The mail merge document(s) have been created and attached to ${sub}.`,
                'Mail merge completed'
              );
            }
          }
        }
      });
  }

  markTaskAsAbandoned(task: Task) {
    this.http
      .put('/api/tasks/abandonTask/' + task.uid, null, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .subscribe((x) => {
        this.getTasks();
        this.eventService.updateTaskStats();
      });
  }

  isMailMergeWizardRequired(task: Task): boolean {
    if (task.taskType) {
      return task.taskType.hideRecord;
    }
    return false;
  }

  hydrate(task: Task): Observable<Task> {
    const hydrate$ = [];

    // Ensure Task passed in is hydrated with objects as they are required for display
    task.taskType = this.taskTypeService.getTaskTypeById(task.taskTypeId);
    task.actionedBy = this.userService.getUserById(task.actionedById);
    task.actionFor = this.userService.getUserById(task.actionForId);
    task.createdBy = this.userService.getUserById(task.createdById);
    task.taskStatusName = this.taskStatusService.getTaskStatusNameById(
      task.taskStatus
    );

    if (task.subjectId && task.subjectId > 0) {
      const index = this.subjectStash.findIndex((i) => i.taskId === task.uid);
      if (index > -1) {
        task.subject = this.subjectStash[index].subject;
        this.subjectStash.splice(index, 1);
      }

      if (task.subject != null) {
        hydrate$.push(
          this.subjectService.hydrate(task.subject).pipe(
            take(1),
            map((hydratedSubject) => {
              task.subject = hydratedSubject;
              task.subjectId = task.subject.id;
              return task;
            })
          )
        );
      }
    } else {
      if (task.subject) {
        task.subjectId = task.subject.id;
      }
    }

    if (hydrate$.length > 0) {
      return forkJoin(hydrate$).pipe(
        take(1),
        map((_) => {
          return new Task(task);
        })
      );
    } else {
      return of(new Task(task));
    }
  }

  save(entity: Task, successMessage?: string): Observable<boolean> {
    // Check ActionedFor user is allowed by linked subject's security

    if (entity.subject && entity.actionForId > 0) {
      return this.subjectService.getUserSecurity(entity.subject).pipe(
        take(1),
        map((allowedUsers: Number[]) => {
          if (allowedUsers.length == 0) return true;
          else
            return allowedUsers.findIndex((i) => i == entity.actionForId) > -1;
        }),

        map((x: boolean) => {
          if (x === true) {
            return this.internalSave(entity, successMessage);
          } else {
            messageService.error(
              "The specified 'Action For' user is not allowed because of the task's parent subject security settings."
            );
            return false;
          }
        })
      );
    } else {
      return of(this.internalSave(entity, successMessage));
    }
  }

  internalSave(entity: Task, successMessage?: string): boolean {
    if (entity.actionedBy) {
      entity.actionedById = entity.actionedBy.id;
    }
    if (entity.actionFor) {
      entity.actionForId = entity.actionFor.id;
    }
    if (entity.createdBy) {
      entity.createdById = entity.createdBy.id;
    }
    if (entity.taskType) {
      entity.taskTypeId = entity.taskType.id;
    }
    if (entity.subject) {
      this.subjectStash.push({ taskId: entity.uid, subject: entity.subject });
      entity.subjectId = entity.subject.id;
    }

    // make sure that any related data objects are 'unset' prior to subject being saved
    // start by caching subject - we need to unset the subject prior to JSON.parse otherwise we may get circular dependency errors
    const subject = entity.subject;
    entity.subject = null;
    const task = JSON.parse(JSON.stringify(entity));
    task.actionedBy = null;
    task.actionFor = null;
    task.createdBy = null;
    task.taskType = null;

    successMessage = successMessage.replace('""', '');
    super.save(task, successMessage);

    // ...and finally restore the subject
    entity.subject = subject;

    return true;
  }

  public getFilters(): ListFilterItem[] {
    return this.filters;
  }

  public applyFilter(tasks: Task[], filter?: ListFilterItem): Task[] {
    var t = this.applyFilterInternal(tasks, filter);
    // console.log('Filtered Tasks Count : ' + t.length);
    return t;
  }

  public applyFilterInternal(tasks: Task[], filter?: ListFilterItem): Task[] {
    if (filter) {
      switch (filter.title.toLowerCase()) {
        default:
        case 'all':
          return tasks;
        case 'open':
           // MRD - according to MG open tasks should include any task which is not completed or abandoned
           return tasks.filter((task) => (!task.taskStatusName) || (!(task.taskStatusName === 'Completed' || task.taskStatusName === 'Abandoned')));
        case 'due':
          return tasks.filter((task) => task.state === 'due');
        case 'completed':
          return tasks.filter((task) => task.taskStatusName === 'Completed');
        case 'late':
          return tasks.filter((task) => task.taskStatusName === 'Late');
        case 'abandoned':
          return tasks.filter((task) => task.taskStatusName === 'Abandoned');
        case 'important':
          return tasks.filter((task) => task.important === true);
        case 'starred':
          return tasks.filter((task) => task.starred === true);
      }
    } else {
      return tasks;
    }
  }

  public applySearchTerm(tasks: Task[], searchTerm: string): Task[] {
    const searchedTasked: Task[] = [];
    const searchText: string = searchTerm.toLowerCase();
    tasks.forEach((task) => {
      if (this.SearchTask(task, searchText)) {
        searchedTasked.push(task);
      }
    });

    // unset any currently selected tasks
    this.onSelected.next(undefined);

    return searchedTasked;
  }

  public SearchTask(task: Task, searchText: string): boolean {
    if (
      (task.summary && task.summary.toLowerCase().indexOf(searchText) > -1) ||
      (task.subject &&
        task.subject.summary.toLowerCase().indexOf(searchText) > -1) ||
      (task.labels && task.labels.toLowerCase().indexOf(searchText) > -1) ||
      (task.actionedBy &&
        new User(task.actionedBy).fullName.toLowerCase().indexOf(searchText) >
          -1) ||
      (task.actionFor &&
        new User(task.actionFor).fullName.toLowerCase().indexOf(searchText) >
          -1) ||
      (task.taskType &&
        task.taskType.name.toLowerCase().indexOf(searchText) > -1) ||
      (task.taskStatusName &&
        task.taskStatusName.toLowerCase().indexOf(searchText) > -1) ||
      (task.createdBy &&
        new User(task.createdBy).fullName.toLowerCase().indexOf(searchText) >
          -1)
    ) {
      return true;
    } else {
      return false;
    }
  }

  public getSortOrders(): ListSortOrderItem[] {
    return this.sortOrders;
  }

  public applySortOrder(tasks: Task[], sortOrder?: ListSortOrderItem): Task[] {
    // stash the sort order for future use
    this.currentSortOrder = sortOrder;

    const ab: number = sortOrder && sortOrder.order ? sortOrder.order : 1;
    const ba: number = ab * -1;

    const title = sortOrder && sortOrder.title ? sortOrder.title : 1;

    switch (title) {
      default:
      case 'Date Due':
        return tasks.sort((a, b) => {
          const x = a.dateDue ? new Date(a.dateDue).getTime() : 0;
          const y = b.dateDue ? new Date(b.dateDue).getTime() : 0;
          return x < y ? ba : x > y ? ab : 0;
        });
      case 'Date Completed':
        return tasks.sort((a, b) => {
          const x = a.dateCompleted ? new Date(a.dateCompleted).getTime() : 0;
          const y = b.dateCompleted ? new Date(b.dateCompleted).getTime() : 0;
          return x < y ? ba : x > y ? ab : 0;
        });
      case 'Date Created':
        return tasks.sort((a, b) => {
          const x = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
          const y = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
          return x < y ? ba : x > y ? ab : 0;
        });
      case 'Task Status':
        return tasks.sort((a, b) => {
          const x = a.taskStatus;
          const y = b.taskStatus;
          return x < y ? ba : x > y ? ab : 0;
        });
    }
  }
}

// tslint:disable-next-line:max-classes-per-file
class TaskComplete {
  task: Task;
  subjectUIDList: string[];
}

// tslint:disable-next-line:max-classes-per-file
class MarkTaskAsCompletedResult {
  taskMarkedAsCompleted: boolean;
  followOnTasksCreated: number;
  mailMergeDocumentsCreated: number;
  mailMergeDocumentUris: string;
  emailMergeDocumentsCreated: number;
  taskUid: string;

  constructor() {
    this.taskMarkedAsCompleted = false;
    this.followOnTasksCreated = 0;
    this.mailMergeDocumentsCreated = 0;
    this.emailMergeDocumentsCreated = 0;
    this.taskUid = '';
  }
}
